/**
 * AIGC Canvas 后端 API 客户端
 * Next.js 原生 API + JWT 认证
 */

import { getApiBase, defaultWorkflowName, getBasePath } from './utils';

// ==================== 常量 ====================

export const TOKEN_KEY = 'aigc-jwt';
export const USER_KEY = 'aigc-user';

// ==================== 401 全局处理 ====================

let authExpiredHandler: (() => void) | null = null;

/**
 * 注册全局 401 处理回调（由 page.tsx 在 mount 时设置）。
 * 当任何 API 返回 401 时，自动 logout 并触发 auth:changed 事件，
 * 使页面切换到登录界面。
 */
export function setAuthExpiredHandler(fn: () => void) {
  authExpiredHandler = fn;
}

/**
 * 带 401 自动处理的 fetch 包装。
 * 行为与原生 fetch 完全一致，额外在响应 401 时触发 handleAuthExpired。
 * 适合 canvasStore 等大量使用 raw fetch 的场景。
 */
export async function authedFetch(
  input: string | URL,
  init?: RequestInit
): Promise<Response> {
  // 自动注入认证与工作组上下文头；调用方显式提供的 header 优先。
  // 注意：不默认设置 Content-Type，避免破坏 FormData 上传的 multipart boundary。
  const merged = new Headers();
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) merged.set('Authorization', `Bearer ${token}`);
    const wgId = localStorage.getItem('aigc-workgroup-id');
    if (wgId && wgId !== 'null' && wgId !== '0') merged.set('X-Workgroup-Id', wgId);
  }
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => merged.set(key, value));
  }
  const res = await fetch(input, { ...init, headers: merged });
  if (res.status === 401) {
    const clone = res.clone();
    const errData = await clone.json().catch(() => ({ error: '' }));
    handleAuthExpired(errData.error || '登录已过期，请重新登录');
  }
  // 扣费成功后全局广播，顶部积分余额等监听方实时刷新。
  // 单点拦截，覆盖 canvasStore 中所有 /credits/deduct 调用（单节点执行、
  // 整流执行、异步视频扣费等），无需逐处修改业务代码。
  if (res.ok && typeof window !== 'undefined') {
    const url = typeof input === 'string' ? input : input.toString();
    if (url.includes('/credits/deduct') || url.includes('/credits/plans')) {
      window.dispatchEvent(new CustomEvent('credits:changed'));
    }
  }
  return res;
}

/**
 * 处理 401 响应：显示 toast 提示 + 清除登录态 + 触发页面切换。
 * 供 apiRequest 和各组件的 raw fetch 共同调用。
 */
export function handleAuthExpired(customMsg?: string) {
  if (typeof window === 'undefined') return;

  // 避免重复触发
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return; // 已经 logout 过了

  // 动态 import toast，避免 SSR 问题
  import('sonner').then(({ toast }) => {
    toast.error(customMsg || '登录已过期，请重新登录', { duration: 4000 });
  });

  // 清除 token
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  window.dispatchEvent(new Event('auth:changed'));

  // 调用注册的回调（如显示登录面板）
  if (authExpiredHandler) authExpiredHandler();
}

// ==================== 类型定义 ====================

export interface WorkflowItem {
  id: number;
  name: string;
  description: string | null;
  data: { nodes: any[]; edges: any[]; savedAt?: string };
  is_template: boolean;
  workgroup_id: number | null;
  /** 分享令牌（2026-10-03 画布分享）：null=未开启分享 */
  share_token?: string | null;
  /**
   * 乐观锁版本号（2026-11-30）。GET 接口返回，PUT 时携带；
   * 服务端版本不一致时 PUT 返回 409 { error:'version_conflict', serverVersion:N }。
   * 旧记录初始为 0；字段缺失（旧接口响应）视为 undefined，走兼容路径。
   */
  sync_version?: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface WorkflowListResponse {
  total: number;
  workflows: WorkflowItem[];
}

export interface UserInfo {
  user_id: string;
  username: string;
  phone: string;
  user_type: string;
  is_paid: boolean;
  phone_verified: boolean;
  created_at?: string;
  /** 头像引用（preset:N / asset:KEY / 绝对 URL / null=未自定义，渲染走 lib/avatar.ts resolveAvatarUrl） */
  avatar_url?: string | null;
  nickname?: string | null;
}

// ==================== 通用请求 ====================

function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('aigc-jwt');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // 工作组上下文
    const wgId = localStorage.getItem('aigc-workgroup-id');
    if (wgId && wgId !== 'null' && wgId !== '0') {
      headers['X-Workgroup-Id'] = wgId;
    }
  }
  return headers;
}

async function apiRequest<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  try {
    const res = await fetch(`${getApiBase()}${path}`, {
      ...options,
      cache: 'no-store',
      headers: {
        ...getHeaders(),
        ...(options?.headers || {}),
      },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: res.statusText }));
      const errMsg = errorData.error || errorData.detail || '请求失败';
      // 401: 全局处理 — toast + logout + 切换登录页
      if (res.status === 401) {
        handleAuthExpired(errMsg);
      }
      throw new ApiError(res.status, errMsg, errorData as Record<string, unknown> | undefined);
    }

    return await res.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(0, '无法连接到服务');
  }
}

export class ApiError extends Error {
  data?: Record<string, unknown>;
  constructor(public status: number, message: string, data?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.data = data;
  }
}

// ==================== 认证相关 ====================

export function isAuthenticated(): boolean {
  if (typeof window === 'undefined') return false;
  return !!localStorage.getItem(TOKEN_KEY);
}

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function getUserInfo(): UserInfo | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserInfo;
  } catch {
    return null;
  }
}

export function getTenantName(): string {
  const user = getUserInfo();
  if (!user) return '';
  // 优先显示手机号，脱敏处理
  if (user.phone && user.phone.length >= 11) {
    return user.phone.slice(0, 3) + '****' + user.phone.slice(-4);
  }
  return user.username || '';
}

export function getTenantId(): string | null {
  const user = getUserInfo();
  return user?.user_id || null;
}

export function logout(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  // Dispatch event so canvas can clear user data
  window.dispatchEvent(new Event('auth:changed'));
}

/** 发送短信验证码 */
export async function sendSmsCode(phone: string): Promise<{ success: boolean; isNewUser?: boolean; message?: string }> {
  const res = await fetch(`${getApiBase()}/auth/sms/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new ApiError(res.status, data.error || '发送失败');
  }
  return { success: true, isNewUser: data.isNewUser, message: data.message };
}

/** 验证短信验证码并登录（inviteCode：邀请活动注册绑定，仅新用户生效；visitorId：UTM 渠道归因，仅新注册落库） */
export async function verifySmsCode(phone: string, code: string, inviteCode?: string): Promise<{ token: string; user: UserInfo }> {
  const { getVisitorId } = await import('@/lib/utmClient');
  const res = await fetch(`${getApiBase()}/auth/sms/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, code, inviteCode: inviteCode || undefined, visitorId: getVisitorId() || undefined }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new ApiError(res.status, data.error || '验证失败');
  }
  // 保存到 localStorage（2026-10-11 iOS Safari 容错：Safari「阻止所有 Cookie」/隐私受限时 setItem 抛
  // SecurityError——吞掉异常会让"登录成功但状态存不进"表现为点击登录毫无反应；这里显式报给用户）
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      // 回验读取：iOS Safari「阻止所有 Cookie」等受限场景 setItem 静默失败/被拒时立刻现形，
      // 避免表现成"登录成功但刷新后没登录"
      if (localStorage.getItem(TOKEN_KEY) !== data.token) throw new Error('token readback mismatch');
    } catch {
      throw new ApiError(res.status, '登录成功，但浏览器禁止保存登录状态：请在 Safari 设置中开启 Cookie（不阻止跨网站/所有 Cookie）后重试');
    }
    // Dispatch event so canvas can load this user's data
    window.dispatchEvent(new Event('auth:changed'));
  }
  return { token: data.token, user: data.user };
}

/** 账号密码登录（用户名或手机号 + 密码）— 私有化部署默认方式 */
export async function passwordLogin(login: string, password: string): Promise<{ token: string; user: UserInfo }> {
  const res = await fetch(`${getApiBase()}/auth/password/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ login, password }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new ApiError(res.status, data.error || '登录失败');
  }
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      // 回验读取（同 verifySmsCode——iOS Safari 受限场景立刻现形）
      if (localStorage.getItem(TOKEN_KEY) !== data.token) throw new Error('token readback mismatch');
    } catch {
      throw new ApiError(res.status, '登录成功，但浏览器禁止保存登录状态：请在 Safari 设置中开启 Cookie（不阻止跨网站/所有 Cookie）后重试');
    }
    window.dispatchEvent(new Event('auth:changed'));
  }
  return { token: data.token, user: data.user };
}

/** 获取当前用户信息（从服务端刷新） */
export async function refreshUserInfo(): Promise<UserInfo | null> {
  if (!isAuthenticated()) return null;
  try {
    const res = await fetch(`${getApiBase()}/auth/me`, {
      headers: getHeaders(),
    });
    if (!res.ok) {
      if (res.status === 401) {
        // 使用 handleAuthExpired 而非 logout — 会显示 toast 提示并触发 auth:changed
        const errorData = await res.json().catch(() => ({ error: '' }));
        handleAuthExpired(errorData.error || '登录已过期，请重新登录');
      }
      return null;
    }
    const data = await res.json();
    if (data.user && typeof window !== 'undefined') {
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    }
    return data.user || null;
  } catch {
    return null;
  }
}

// ==================== 工作流 API ====================

export const workflowApi = {
  list: async (params?: { is_template?: boolean; limit?: number; offset?: number }): Promise<WorkflowListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.is_template !== undefined) searchParams.set('is_template', String(params.is_template));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return apiRequest<WorkflowListResponse>(`/workflows${query ? `?${query}` : ''}`);
  },

  create: async (data: {
    name: string;
    description?: string;
    data: { nodes: any[]; edges: any[] };
    is_template?: boolean;
  }): Promise<{ success: boolean; data: { id: number } }> => {
    return apiRequest('/workflows', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  get: async (id: number): Promise<WorkflowItem> => {
    return apiRequest<WorkflowItem>(`/workflows/${id}`);
  },

  update: async (
    id: number,
    data: { name?: string; description?: string; data?: Record<string, unknown>; is_template?: boolean; sync_version?: number }
  ): Promise<{ success: boolean; data: { id: number; sync_version?: number } }> => {
    return apiRequest(`/workflows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number): Promise<{ success: boolean; message: string }> => {
    return apiRequest(`/workflows/${id}`, {
      method: 'DELETE',
    });
  },

  /** 工作流跨空间转移（targetWorkgroupId=null 转到个人空间；当前空间由 X-Workgroup-Id 头决定） */
  transfer: async (
    id: number,
    targetWorkgroupId: number | null
  ): Promise<{ success: boolean; message: string; data: { moved: { execLogs: number; assets: number } } }> => {
    return apiRequest(`/workflows/${id}/transfer`, {
      method: 'POST',
      body: JSON.stringify({ targetWorkgroupId }),
    });
  },

  // ── 画布分享（2026-10-03 share_token）──
  /** 开启/关闭分享。开启返回 shareToken（复用已有），关闭置空 */
  setShare: async (
    id: number,
    enabled: boolean
  ): Promise<{ success: boolean; data: { shareToken: string | null } }> => {
    return apiRequest(`/workflows/${id}/share`, {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    });
  },

  /** 令牌只读查看分享画布（登录用户均可打开；isOwner=本人分享的） */
  getShared: async (token: string): Promise<{
    success: boolean;
    data: {
      id: number;
      name: string;
      description: string | null;
      data: { nodes?: unknown[]; edges?: unknown[] };
      ownerNickname: string | null;
      isOwner: boolean;
      updatedAt: string;
    };
  }> => {
    return apiRequest(`/workflows/shared/${encodeURIComponent(token)}`);
  },

  /** 克隆分享画布为本人当前空间下的副本工作流，返回新工作流 ID */
  cloneShared: async (token: string): Promise<{ success: boolean; data: { id: number } }> => {
    return apiRequest(`/workflows/shared/${encodeURIComponent(token)}/clone`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  save: async (data: {
    id?: number;
    name: string;
    description?: string;
    workflowData: { nodes: any[]; edges: any[] };
    /** 乐观锁版本号（2026-11-30）：有则携带，服务端版本不一致时 409 */
    syncVersion?: number;
  }): Promise<{ id: number; sync_version?: number }> => {
    if (data.id) {
      const result = await workflowApi.update(data.id, {
        name: data.name,
        description: data.description,
        data: data.workflowData,
        sync_version: data.syncVersion,
      });
      return { id: result.data.id, sync_version: result.data.sync_version };
    } else {
      const result = await workflowApi.create({
        name: data.name,
        description: data.description,
        data: data.workflowData,
        is_template: false,
      });
      return { id: result.data.id };
    }
  },

  /** Create a draft workflow immediately to get a workflow_id for associating execution logs and assets */
  createDraft: async (name?: string): Promise<{ id: number }> => {
    const result = await workflowApi.create({
      name: name?.trim() || defaultWorkflowName(),
      description: '草稿',
      data: { nodes: [], edges: [] },
      is_template: false,
    });
    return { id: result.data.id };
  },
};

/** Standalone function: create a draft workflow immediately to get a workflow_id */
export async function createDraftWorkflow(name?: string): Promise<{ id: number }> {
  return workflowApi.createDraft(name);
}

// ==================== 健康检查 ====================

export const healthCheck = async (): Promise<boolean> => {
  try {
    const res = await fetch(`${getApiBase()}/health`);
    return res.ok;
  } catch {
    return false;
  }
};

// ==================== 执行历史 ====================

export interface ExecutionLogItem {
  id: number;
  status: string;
  nodeCount: number;
  workflow_name?: string;
  created_at?: string;
  duration_ms?: number;
  startTime: string;
  endTime: string | null;
  duration: number;
  error_message?: string;
  logs: Array<{ time: string; nodeId?: string; message: string; type: string }>;
  node_results?: Record<string, {
    label?: string;
    type?: string;
    status?: string;
    output_data?: unknown;
    error_message?: string;
    duration_ms?: number;
  }>;
}

export interface MediaAssetItem {
  id: number;
  type: string;
  url: string | null;
  rawUrl?: string;
  storageKey: string;
  nodeLabel: string;
  prompt: string;
  source: string;
  originalName: string;
  createdAt: string | null;
}

export async function fetchExecutionLogs(options?: { limit?: number; workflowId?: number | null; cursor?: string }): Promise<{ logs: ExecutionLogItem[]; mediaAssets: MediaAssetItem[]; nextCursor?: string }> {
  const token = getToken();
  if (!token) throw new ApiError(401, '未登录');
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.workflowId != null) params.set('workflowId', String(options.workflowId));
  if (options?.cursor) params.set('cursor', options.cursor);
  const res = await authedFetch(`${getApiBase()}/execution-logs?${params}`, {
    headers: getHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(res.status, err.error || '获取执行历史失败');
  }
  const data = await res.json();
  return {
    logs: data.logs || [],
    mediaAssets: data.mediaAssets || [],
    nextCursor: data.nextCursor || undefined,
  };
}

export async function saveExecutionLog(data: {
  workflow_name: string;
  status: string;
  node_count: number;
  node_results: Record<string, unknown>;
  error_message?: string;
  duration_ms?: number;
  workflow_id?: number | null;
}): Promise<{ success: boolean; id: number }> {
  const token = getToken();
  if (!token) throw new ApiError(401, '未登录');
  const res = await authedFetch(`${getApiBase()}/execution-logs`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(res.status, err.error || '保存执行记录失败');
  }
  return res.json();
}

// Associate user's unlinked logs and assets to a workflow (after save)
export async function linkWorkflowToHistory(workflowId: string): Promise<void> {
  const token = getToken();
  if (!token) return;
  const res = await authedFetch(`${getApiBase()}/execution-logs?workflowId=${encodeURIComponent(workflowId)}`, {
    method: 'PATCH',
    headers: getHeaders(),
  });
  if (!res.ok) {
    console.warn('linkWorkflowToHistory failed:', res.status);
  }
}

// ==================== 生成资产 ====================

/**
 * 画布素材引用登记（幂等，best-effort）。
 * 素材发送到画布 / 拖入画布后立即调用，
 * 使个人空间与画布媒体页无需等待 30s 自动保存即可看到该素材。
 */
export async function registerAssetOnCanvas(data: {
  url: string;
  type: 'image' | 'video' | 'audio';
  nodeId?: string;
  workflowId?: number | null;
  name?: string;
  prompt?: string;
}): Promise<void> {
  if (!getToken()) return;
  try {
    const res = await authedFetch(`${getApiBase()}/assets/register`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      console.warn('[registerAssetOnCanvas] HTTP', res.status);
    }
  } catch (e) {
    console.warn('[registerAssetOnCanvas] failed:', e);
  }
}

export interface AssetItem {
  id: number;
  type: 'image' | 'video' | 'audio' | 'text';
  url: string | null;
  originalUrl: string | null;
  storageKey: string | null;
  nodeId: string | null;
  workflowId: number | null;
  nodeLabel: string | null;
  prompt: string | null;
  metadata: Record<string, unknown>;
  topCategory: string | null;
  folderId: number | null;
  uploaderId: string | null;
  createdAt: string | null;
  serveUrl: string;
}

export async function fetchAssets(options?: {
  type?: string; limit?: number;
  topCategory?: string; folderId?: number | 'root'; tag?: string; uploadedBy?: string;
}): Promise<AssetItem[]> {
  const token = getToken();
  if (!token) throw new ApiError(401, '未登录');
  const params = new URLSearchParams();
  if (options?.type) params.set('type', options.type);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.topCategory) params.set('topCategory', options.topCategory);
  if (options?.folderId !== undefined) params.set('folderId', String(options.folderId));
  if (options?.tag) params.set('tag', options.tag);
  if (options?.uploadedBy) params.set('uploadedBy', options.uploadedBy);
  const res = await authedFetch(`${getApiBase()}/assets/list?${params}`, {
    headers: getHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(res.status, err.error || '获取资产失败');
  }
  const data = await res.json();
  const assets = data.assets || [];
  // Add serveUrl for permanent access (uses storageKey from API)
  return assets.map((a: Record<string, unknown>) => ({
    id: a.id as number,
    type: a.type as AssetItem['type'],
    url: (a.url as string) || null,
    originalUrl: (a.originalUrl as string) || null,
    storageKey: (a.storageKey as string) || null,
    nodeId: (a.nodeId as string) || null,
    workflowId: (a.workflowId as number) || null,
    nodeLabel: (a.nodeLabel as string) || null,
    prompt: (a.prompt as string) || null,
    metadata: (a.metadata as Record<string, unknown>) || {},
    topCategory: (a.topCategory as string) || null,
    folderId: (a.folderId as number) || null,
    uploaderId: (a.uploaderId as string) || null,
    createdAt: (a.createdAt as string) || null,
    // Use the signed serve URL from API response (includes HMAC token)
    serveUrl: (a.url as string) || '',
  })) as AssetItem[];
}

export async function saveAsset(data: {
  type: string;
  url: string;
  node_id?: string;
  workflow_id?: number;
  metadata?: Record<string, unknown>;
  topCategory?: string;
  folderId?: number | null;
}): Promise<{ success: boolean; id: number }> {
  const token = getToken();
  if (!token) throw new ApiError(401, '未登录');
  const res = await authedFetch(`${getApiBase()}/assets/list`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(res.status, err.error || '保存资产失败');
  }
  return res.json();
}

export async function deleteAsset(assetId: number): Promise<{ success: boolean }> {
  const token = getToken();
  if (!token) throw new ApiError(401, '未登录');
  const res = await authedFetch(`${getApiBase()}/assets/${assetId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(res.status, err.error || '删除资产失败');
  }
  return res.json();
}

export async function deleteExecutionLog(executionId: number): Promise<{ success: boolean }> {
  const token = getToken();
  if (!token) throw new ApiError(401, '未登录');
  const res = await authedFetch(`${getApiBase()}/execution-logs/${executionId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(res.status, err.error || '删除失败');
  }
  return { success: true };
}

export async function rerunExecution(executionId: number): Promise<{ success: boolean }> {
  const token = getToken();
  if (!token) throw new ApiError(401, '未登录');
  const res = await authedFetch(`${getApiBase()}/execution-logs/${executionId}/rerun`, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(res.status, err.error || '重跑失败');
  }
  return { success: true };
}

// ==================== 管理员 API ====================

export interface AdminUser {
  user_id: string;
  username: string;
  nickname: string | null;
  avatar_url: string | null;
  phone: string | null;
  user_type: string;
  is_paid: boolean;
  phone_verified: boolean;
  remark: string | null;
  created_at: string;
  updated_at: string;
  balance: number;
  plan?: string;
  /** 套餐显示名（aigc_plans.name，后台改名即时生效；到期降级为免费版） */
  plan_name?: string | null;
  /** 订阅已到期（plan 已按生效口径降级为 free） */
  plan_expired?: boolean;
  /** 到期前订阅的套餐显示名（仅 plan_expired=true 时有值） */
  subscribed_plan_name?: string | null;
  total_consumed?: number;
  total_recharged?: number;
}

export interface AdminSetting {
  id: number;
  setting_key: string;
  setting_value: string | null;
  category: string;
  description: string | null;
  is_secret: boolean;
  is_configured: boolean;
  updated_at: string;
  updated_by: string | null;
}

export interface BillingRecord {
  id: number;
  user_id: string;
  amount: number;
  balance_after: number;
  type: string;
  description: string | null;
  created_at: string;
  created_by: string | null;
  phone: string | null;
  username: string | null;
}

function adminHeaders(): HeadersInit {
  const token = getToken();
  if (!token) throw new ApiError(401, '未登录');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/* ── 推广中心（2026-10-13 下放用户端）：链接管理 + pt 归因数据 ── */
export const promoteApi = {
  list: async (gran: 'today' | 'daily' | 'weekly' | 'monthly' = 'weekly') => {
    const res = await authedFetch(`${getApiBase()}/promote/links?gran=${gran}`);
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取推广数据失败');
    return res.json();
  },
  create: async (input: { name: string; utmSource: string; utmMedium: string; utmCampaign?: string; utmContent?: string }) => {
    const res = await authedFetch(`${getApiBase()}/promote/links`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '创建失败');
    return res.json();
  },
  archive: async (id: number) => {
    const res = await authedFetch(`${getApiBase()}/promote/links?id=${id}`, { method: 'DELETE' });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '归档失败');
    return res.json();
  },
};

export const adminApi = {
  // 推广员维度报表（2026-10-13 推广中心下放）：汇总 + 链接明细（pt 精确归属）
  getUtmPartners: async (gran: 'today' | 'daily' | 'weekly' | 'monthly' = 'weekly') => {
    const res = await authedFetch(`${getApiBase()}/admin/utm-partners?gran=${gran}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取推广员数据失败');
    return res.json();
  },
  // UTM 归因漏斗（2026-10-13）：近 N 周 来源×周 聚合
  getUtmFunnel: async (weeks: number) => {
    const res = await authedFetch(`${getApiBase()}/admin/utm-funnel?weeks=${weeks}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取漏斗数据失败');
    return res.json();
  },
  // UTM 归因漏斗 · 今日实时（2026-10-14）：上海今日 0 点起按来源聚合
  getUtmFunnelToday: async () => {
    const res = await authedFetch(`${getApiBase()}/admin/utm-funnel?mode=today`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取今日漏斗数据失败');
    return res.json();
  },
  // 推广奖励明细（2026-10-24 用户定稿）：推广者账户信息 + 奖励流水（可按推广者过滤）
  getPartnerRewards: async (params: { page?: number; pageSize?: number; partnerUserId?: string; type?: 'register' | 'pay' | 'first_plan' | 'invite_promo'; q?: string; days?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params.partnerUserId) qs.set('partnerUserId', params.partnerUserId);
    if (params.type) qs.set('type', params.type);
    if (params.q) qs.set('q', params.q);
    if (params.days) qs.set('days', String(params.days));
    const res = await authedFetch(`${getApiBase()}/admin/partner-rewards?${qs.toString()}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取推广奖励明细失败');
    return res.json();
  },

  // 仪表盘统计
  getStats: async () => {
    const res = await authedFetch(`${getApiBase()}/admin/stats`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取统计失败');
    return res.json();
  },

  // 模型显隐：全量清单 + 当前隐藏列表（2026-08-29 模型管理 Tab）
  getModelVisibility: async () => {
    const res = await authedFetch(`${getApiBase()}/admin/models/visibility`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取模型清单失败');
    return res.json();
  },

  // 模型显隐：保存隐藏列表（全量覆盖语义）
  saveModelVisibility: async (hidden: string[]) => {
    const res = await authedFetch(`${getApiBase()}/admin/models/visibility`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify({ hidden }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '保存模型显隐失败');
    return res.json();
  },

  // 模型行标签：保存标签配置（与显隐同 route，badges 全量覆盖；hidden 必须同时携带当前隐藏列表，
  // 否则 PUT 的 hidden 全量覆盖语义会意外清空已隐藏的模型；2026-09-21）
  saveModelBadges: async (
    hidden: string[],
    badges: Record<string, { label: string; tone?: 'promo' | 'new'; tooltip?: string }>
  ) => {
    const res = await authedFetch(`${getApiBase()}/admin/models/visibility`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify({ hidden, badges }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '保存模型标签失败');
    return res.json();
  },

  // 积分消耗按模型/渠道 × 天/月 统计（折线图数据源）
  getModelUsage: async (params?: { granularity?: 'day' | 'month'; days?: number; months?: number }) => {
    const sp = new URLSearchParams();
    if (params?.granularity) sp.set('granularity', params.granularity);
    if (params?.days) sp.set('days', String(params.days));
    if (params?.months) sp.set('months', String(params.months));
    const qs = sp.toString();
    const res = await authedFetch(`${getApiBase()}/admin/model-usage${qs ? `?${qs}` : ''}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取消耗统计失败');
    return res.json();
  },

  // 用户管理
  listUsers: async (params?: { search?: string; user_type?: string; user_ids?: string; date_from?: string; page?: number; page_size?: number }) => {
    const sp = new URLSearchParams();
    if (params?.search) sp.set('search', params.search);
    if (params?.user_type) sp.set('user_type', params.user_type);
    if (params?.user_ids) sp.set('user_ids', params.user_ids);
    if (params?.date_from) sp.set('date_from', params.date_from);
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    const res = await authedFetch(`${getApiBase()}/admin/users?${sp}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取用户列表失败');
    return res.json();
  },

  getUser: async (userId: string) => {
    const res = await authedFetch(`${getApiBase()}/admin/users/${userId}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取用户详情失败');
    return res.json();
  },

  updateUser: async (userId: string, data: { user_type?: string; is_paid?: boolean; nickname?: string; phone?: string; remark?: string }) => {
    const res = await authedFetch(`${getApiBase()}/admin/users/${userId}`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '更新用户失败');
    return res.json();
  },

  deleteUser: async (userId: string) => {
    const res = await authedFetch(`${getApiBase()}/admin/users/${userId}`, {
      method: 'DELETE',
      headers: adminHeaders(),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '删除用户失败');
    return res.json();
  },

  /** 创建账号密码用户（私有化部署 admin 开通账号） */
  createUser: async (data: { username: string; password: string; nickname?: string; phone?: string; user_type?: string; bonus_credits?: number }) => {
    const res = await authedFetch(`${getApiBase()}/admin/users`, {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '创建用户失败');
    return res.json();
  },

  // 画布任务管理（异步任务体系）
  listCanvasTasks: async (params?: { page?: number; page_size?: number; q?: string; status?: string; task_type?: string; model?: string; user_id?: string; date_from?: string; date_to?: string }) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    if (params?.q) sp.set('q', params.q);
    if (params?.status) sp.set('status', params.status);
    if (params?.task_type) sp.set('task_type', params.task_type);
    if (params?.model) sp.set('model', params.model);
    if (params?.user_id) sp.set('user_id', params.user_id);
    if (params?.date_from) sp.set('date_from', params.date_from);
    if (params?.date_to) sp.set('date_to', params.date_to);
    const res = await authedFetch(`${getApiBase()}/admin/canvas-tasks?${sp}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取任务列表失败');
    return res.json();
  },

  getCanvasTaskStats: async (days = 7) => {
    const res = await authedFetch(`${getApiBase()}/admin/canvas-tasks?action=stats&days=${days}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取任务统计失败');
    return res.json();
  },

  // 实时并发/排队统计（2026-08-15 排队机制）
  getCanvasTaskConcurrency: async () => {
    const res = await authedFetch(`${getApiBase()}/admin/canvas-tasks?action=concurrency`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取并发统计失败');
    return res.json();
  },

  // 全局设置
  getSettings: async () => {
    const res = await authedFetch(`${getApiBase()}/admin/settings`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取设置失败');
    return res.json();
  },

  updateSettings: async (settings: Array<{ key: string; value: string }>) => {
    const res = await authedFetch(`${getApiBase()}/admin/settings`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify({ settings }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '更新设置失败');
    return res.json();
  },

  // 计费管理
  listBilling: async (params?: { user_id?: string; type?: string; page?: number; page_size?: number }) => {
    const sp = new URLSearchParams();
    if (params?.user_id) sp.set('user_id', params.user_id);
    if (params?.type) sp.set('type', params.type);
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    const res = await authedFetch(`${getApiBase()}/admin/billing?${sp}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取充值记录失败');
    return res.json();
  },

  recharge: async (data: { user_id: string; amount: number; type: 'recharge' | 'deduct'; description?: string }) => {
    const res = await authedFetch(`${getApiBase()}/admin/billing`, {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '充值操作失败');
    return res.json();
  },

  // 积分管理
  getCreditsOverview: async () => {
    const res = await authedFetch(`${getApiBase()}/admin/credits?section=overview`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取积分概览失败');
    return res.json();
  },

  getCreditAccounts: async (params?: { search?: string; page?: number; page_size?: number }) => {
    const sp = new URLSearchParams();
    if (params?.search) sp.set('search', params.search);
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    const res = await authedFetch(`${getApiBase()}/admin/credits?section=accounts&${sp}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取积分账户失败');
    return res.json();
  },

  getCreditLogs: async (params?: { user_id?: string; page?: number; page_size?: number }) => {
    const sp = new URLSearchParams();
    if (params?.user_id) sp.set('user_id', params.user_id);
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    const res = await authedFetch(`${getApiBase()}/admin/credits?section=logs&${sp}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取积分记录失败');
    return res.json();
  },

  // 账单总览：按用户 × 日/月聚合消耗积分 + API 成本
  getBillingOverview: async (params?: { granularity?: 'day' | 'month'; days?: number; months?: number; user_id?: string; search?: string; page?: number; page_size?: number; sort_by?: string; sort_order?: 'asc' | 'desc' }) => {
    const sp = new URLSearchParams({ section: 'billing' });
    if (params?.granularity) sp.set('granularity', params.granularity);
    if (params?.days) sp.set('days', String(params.days));
    if (params?.months) sp.set('months', String(params.months));
    if (params?.user_id) sp.set('user_id', params.user_id);
    if (params?.search) sp.set('search', params.search);
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    if (params?.sort_by) sp.set('sort_by', params.sort_by);
    if (params?.sort_order) sp.set('sort_order', params.sort_order);
    const res = await authedFetch(`${getApiBase()}/admin/credits?${sp}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取账单总览失败');
    return res.json();
  },

  // 账单详情：某用户某日/月的模型维度消耗明细
  getBillingDetail: async (params: { granularity: 'day' | 'month'; bucket: string; user_id: string }) => {
    const sp = new URLSearchParams({ section: 'billing-detail', granularity: params.granularity, bucket: params.bucket, user_id: params.user_id });
    const res = await authedFetch(`${getApiBase()}/admin/credits?${sp}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取账单明细失败');
    return res.json();
  },

  updateCreditConfig: async (data: { node_type: string; credits_per_use: number; billing_mode: string; description?: string; is_active: boolean }) => {
    const res = await authedFetch(`${getApiBase()}/admin/credits`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify({ action: 'update_config', ...data }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '更新积分配置失败');
    return res.json();
  },

  updateVideoPricing: async (data: { video_mode: string; credits_per_second: number; api_cost_per_second: number; label?: string; is_active: boolean }) => {
    const res = await authedFetch(`${getApiBase()}/admin/credits`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify({ action: 'update_video_pricing', ...data }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '更新视频定价失败');
    return res.json();
  },

  updateTtsPricing: async (data: { tts_model: string; credits_per_char: number; yuan_per_10k_chars: number; label?: string; is_active: boolean }) => {
    const res = await authedFetch(`${getApiBase()}/admin/credits`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify({ action: 'update_tts_pricing', ...data }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '更新 TTS 定价失败');
    return res.json();
  },

  // 模型成本配置（aigc_model_costs，后台核算用，不影响积分扣费）
  getModelCosts: async (costType?: string) => {
    const sp = new URLSearchParams({ section: 'model-costs' });
    if (costType) sp.set('cost_type', costType);
    const res = await authedFetch(`${getApiBase()}/admin/credits?${sp}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取模型成本配置失败');
    return res.json();
  },

  updateModelCost: async (data: { id: number; unit_prices?: Record<string, number | null>; calc_meta?: Record<string, unknown>; label?: string; is_active?: boolean }) => {
    const res = await authedFetch(`${getApiBase()}/admin/credits`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify({ action: 'update_model_cost', ...data }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '更新模型成本配置失败');
    return res.json();
  },

  rechargeCredits: async (data: { user_id: string; amount: number; description?: string }) => {
    const res = await authedFetch(`${getApiBase()}/admin/credits`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify({ action: 'recharge', ...data }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '积分充值失败');
    return res.json();
  },

  // 在线用户
  getOnlineUsers: async () => {
    const res = await authedFetch(`${getApiBase()}/admin/online`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取在线用户失败');
    return res.json();
  },

  // 访问统计（2026-10-11 首页 PV/UV 分析）
  getVisitStats: async (days: number) => {
    const res = await authedFetch(`${getApiBase()}/admin/visit-stats?days=${days}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取访问统计失败');
    return res.json();
  },

  // 并发监控（2026-10-17）：每小时模型并发 / 在线人数 趋势（5 分钟快照聚合）
  getHourlyMetrics: async (range: '24h' | '7d' | '30d') => {
    const res = await authedFetch(`${getApiBase()}/admin/hourly-metrics?range=${range}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取并发监控数据失败');
    return res.json();
  },

  // 访问明细分页（时间倒序，独立翻页）
  getVisitLogs: async (page: number, pageSize = 50) => {
    const res = await authedFetch(`${getApiBase()}/admin/visit-logs?page=${page}&pageSize=${pageSize}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取访问明细失败');
    return res.json();
  },

  // 用户反馈管理（2026-08-15）
  listFeedbacks: async (params?: { page?: number; page_size?: number; status?: string; type?: string; q?: string }) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    if (params?.status) sp.set('status', params.status);
    if (params?.type) sp.set('type', params.type);
    if (params?.q) sp.set('q', params.q);
    const res = await authedFetch(`${getApiBase()}/admin/feedbacks?${sp}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取反馈列表失败');
    return res.json();
  },

  handleFeedback: async (id: number, action: 'reply' | 'reopen', handleNote?: string) => {
    const res = await authedFetch(`${getApiBase()}/admin/feedbacks`, {
      method: 'PATCH',
      headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, action, handleNote }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '处理反馈失败');
    return res.json();
  },

  // 充值统计
  getRevenue: async (params?: {
    period?: number; start?: string; end?: string;
    type?: string; method?: string; status?: string; q?: string;
    page?: number; page_size?: number; export?: boolean;
  }) => {
    const sp = new URLSearchParams();
    if (params?.period) sp.set('period', String(params.period));
    if (params?.start) sp.set('start', params.start);
    if (params?.end) sp.set('end', params.end);
    if (params?.type) sp.set('type', params.type);
    if (params?.method) sp.set('method', params.method);
    if (params?.status) sp.set('status', params.status);
    if (params?.q) sp.set('q', params.q);
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    if (params?.export) sp.set('export', '1');
    const res = await authedFetch(`${getApiBase()}/admin/revenue?${sp}`, { headers: adminHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取充值统计失败');
    return res.json();
  },

  // 获取指定用户的积分账户 + 积分消耗/充值明细
  getUserCredits: async (
    userId: string,
    params?: { page?: number; page_size?: number; type?: string; node_type?: string }
  ) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    if (params?.type) sp.set('type', params.type);
    if (params?.node_type) sp.set('node_type', params.node_type);
    const qs = sp.toString();
    const res = await authedFetch(
      `${getApiBase()}/admin/users/${encodeURIComponent(userId)}/credits${qs ? `?${qs}` : ''}`,
      { headers: adminHeaders() }
    );
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取用户积分明细失败');
    return res.json();
  },
};

// === 积分 API (用户端) ===
export const creditsApi = {
  getBalance: async () => {
    const res = await authedFetch(`${getApiBase()}/credits/balance`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取余额失败');
    return res.json();
  },

  getLogs: async (params?: { page?: number; page_size?: number }) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    const res = await authedFetch(`${getApiBase()}/credits/logs?${sp}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取积分记录失败');
    return res.json();
  },

  getPlans: async () => {
    const res = await authedFetch(`${getApiBase()}/credits/plans`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取套餐失败');
    return res.json();
  },

  subscribe: async (planCode: string, period: string) => {
    const res = await authedFetch(`${getApiBase()}/credits/plans`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify({ action: 'subscribe', plan_code: planCode, period }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '订阅失败');
    return res.json();
  },

  purchase: async (credits: number) => {
    const res = await authedFetch(`${getApiBase()}/credits/plans`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify({ action: 'purchase', credits }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '购买失败');
    return res.json();
  },
};

// ==================== 资产管理 API ====================

export const assetApi = {
  // 查询资产列表
  list: async (params?: { type?: string; page?: number; page_size?: number }) => {
    const query = new URLSearchParams();
    if (params?.type) query.set('type', params.type);
    if (params?.page) query.set('page', String(params.page));
    if (params?.page_size) query.set('page_size', String(params.page_size));
    const qs = query.toString();
    const res = await authedFetch(`${getApiBase()}/assets${qs ? `?${qs}` : ''}`, { headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取资产列表失败');
    return res.json() as Promise<{ success: true; data: { items: AssetItem[]; total: number; page: number; page_size: number } }>;
  },

  // 获取单个资产详情
  get: async (id: number) => {
    const res = await authedFetch(`${getApiBase()}/assets/${id}`, { headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取资产详情失败');
    return res.json() as Promise<{ success: true; data: AssetItem }>;
  },

  // 删除资产（同时删除TOS文件）
  delete: async (id: number) => {
    const res = await authedFetch(`${getApiBase()}/assets/${id}`, { method: 'DELETE', headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '删除资产失败');
    return res.json() as Promise<{ success: true; data: { deleted: true; tos_deleted: boolean } }>;
  },

  // 批量删除资产
  batchDelete: async (ids: number[]) => {
    const res = await authedFetch(`${getApiBase()}/assets`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '批量删除失败');
    return res.json() as Promise<{ success: true; data: { deleted: number; errors: string[] } }>;
  },

  // 获取资产永久访问URL
  serveUrl: (key: string) => {
    return `${getApiBase()}/assets/serve?key=${encodeURIComponent(key)}`;
  },
};

// ==================== 支付 API ====================

export interface PlanInfo {
  id: number;
  code: string;
  name: string;
  price: number;
  duration_months: number;
  credits: number;
  features: string[];
  is_active: boolean;
}

export interface OrderInfo {
  orderNo: string;
  status: 'pending' | 'paid' | 'failed' | 'closed' | 'refunded';
  amount: number;
  payMethod: 'alipay' | 'wechat';
  orderType: 'plan' | 'credits';
  credits: number | null;
  planId: number | null;
  paidAt: string | null;
  createdAt: string;
}

/** 前端公开运行时配置（部署形态开关） */
export interface BrandingConfig {
  /** 品牌名（登录页/工作台/落地页） */
  name: string;
  /** LOGO base64 data URI；空串 = 使用内置默认 LOGO */
  logo: string;
  /** 站点副标题（浏览器标签标题「品牌 - 副标题」）；空串 = 标签只显示品牌名 */
  subtitle: string;
  /** 登录页主标题；空串 = 显示品牌名 */
  loginTitle: string;
  /** 登录页副标题 */
  loginSubtitle: string;
}

export interface PublicConfig {
  paymentEnabled: boolean;
  smsAuthEnabled: boolean;
  passwordAuthEnabled: boolean;
  registerEnabled: boolean;
  /** 画布免费版顶栏「邀请有礼」入口显示开关（admin 后台可控） */
  inviteEntryEnabled: boolean;
  branding: BrandingConfig;
}

let publicConfigCache: PublicConfig | null = null;

/** 获取公开配置（带内存缓存；失败时按线上默认全开兜底） */
export async function getPublicConfig(): Promise<PublicConfig> {
  if (publicConfigCache) return publicConfigCache;
  try {
    const res = await fetch(`${getApiBase()}/public/config`);
    if (!res.ok) throw new Error('fetch failed');
    const json = await res.json();
    publicConfigCache = json.data as PublicConfig;
    return publicConfigCache;
  } catch {
    return {
      paymentEnabled: true,
      smsAuthEnabled: true,
      passwordAuthEnabled: false,
      registerEnabled: true,
      inviteEntryEnabled: true,
      branding: { name: '深度智像', logo: '', subtitle: '', loginTitle: '', loginSubtitle: 'AI 内容生成工作流平台' },
    };
  }
}

export const paymentApi = {
  /** 获取可用套餐列表（从 aigc_plans 读取） */
  getPlans: async () => {
    const res = await authedFetch(`${getApiBase()}/credits/plans`, { headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取套餐失败');
    return res.json();
  },

  /** 创建支付宝电脑网站支付订单（period: 'monthly'|'yearly'，订阅年付时传 'yearly'） */
  createAlipay: async (data: { planId?: number; period?: string; credits?: number; workgroupId?: number; seats?: number }) => {
    const res = await authedFetch(`${getApiBase()}/payment/alipay/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))).error || '创建支付宝订单失败';
      throw new ApiError(res.status, err);
    }
    return res.json() as Promise<{
      success: true;
      data: { orderNo: string; amount: number; payMethod: 'alipay'; payFormHtml: string; outTradeNo: string };
    }>;
  },

  /** 创建微信支付 Native 订单（period: 'monthly'|'yearly'，订阅年付时传 'yearly'） */
  createWechat: async (data: { planId?: number; period?: string; credits?: number; workgroupId?: number; seats?: number }) => {
    const res = await authedFetch(`${getApiBase()}/payment/wechat/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))).error || '创建微信订单失败';
      throw new ApiError(res.status, err);
    }
    return res.json() as Promise<{
      success: true;
      data: { orderNo: string; amount: number; payMethod: 'wechat'; codeUrl: string };
    }>;
  },

  /** 查询订单状态（轮询用） */
  getOrder: async (orderNo: string) => {
    const res = await authedFetch(`${getApiBase()}/payment/order/${encodeURIComponent(orderNo)}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '查询订单失败');
    return res.json() as Promise<{ success: true; data: OrderInfo }>;
  },

  /** 关闭未支付订单 */
  closeOrder: async (orderNo: string) => {
    const res = await authedFetch(`${getApiBase()}/payment/close`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify({ orderNo }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '关闭订单失败');
    return res.json();
  },

  /** 获取用户订单列表 */
  getOrders: async (limit = 20) => {
    const res = await authedFetch(`${getApiBase()}/payment/orders?limit=${limit}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取订单列表失败');
    return res.json() as Promise<{ success: true; data: OrderInfo[] }>;
  },

  /** 主动查询微信支付订单状态（异步通知 backup） */
  queryWechatOrder: async (orderNo: string) => {
    const res = await authedFetch(`${getApiBase()}/payment/wechat/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify({ orderNo }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '查询微信订单失败');
    return res.json() as Promise<{
      success: true;
      data: { status: 'pending' | 'paid' | 'failed' | 'closed'; amount: number };
    }>;
  },
};

// ==================== 工作组 API ====================

export interface WorkgroupInfo {
  id: number;
  name: string;
  owner_user_id: string;
  description: string | null;
  max_seats: number;
  used_seats: number;
  status: string;
  role: string;
  created_at: string;
  /** 实时推算的订阅状态：active/grace/expired/archived */
  space_status?: 'active' | 'grace' | 'expired' | 'archived';
  subscription_expires_at?: string | null;
  grace_remaining_ms?: number;
  archive_in_ms?: number;
  /** 所有者显示名（昵称/用户名/脱敏手机号，2026-09-03 起随列表返回） */
  owner_name?: string | null;
  /** 当前用户是否为该组创建者 */
  is_owner?: boolean;
}

/** 团队积分池信息 */
export interface WorkgroupCreditsInfo {
  balance: number;
  free: number;
  subscription: number;
  purchased: number;
  totalConsumed: number;
  totalRecharged: number;
  myRole: string;
  canManage: boolean;
  logs: Array<{
    id: number;
    user_id: string;
    operator_name: string | null;
    amount: number;
    balance_after: number;
    type: string;
    node_type: string | null;
    description: string | null;
    created_at: string;
  }>;
}

/** 空间总览（个人/团队通用） */
export interface WorkspaceOverview {
  spaceType: 'personal' | 'workgroup';
  space: {
    id: number | null;
    name: string;
    status: 'active' | 'grace' | 'expired' | 'archived';
    subscriptionExpiresAt?: string | null;
    graceRemainingMs?: number;
    archiveInMs?: number;
    myRole: string | null;
  };
  storage: {
    usedBytes: number;
    fileCount: number;
    quotaBytes: number;
    usageRatio: number;
    /** 按媒体类型分类统计（image/video/audio 等） */
    breakdown?: Record<string, { count: number; bytes: number }>;
  };
  credits: {
    pool: 'personal' | 'workgroup';
    /** 团队空间且本人为 editor/viewer 时 true：按文档 4.5.3 普通成员不可见团队池总额 */
    restricted?: boolean;
    balance: number;
    free: number;
    subscription: number;
    purchased: number;
    totalConsumed: number;
    plan?: string;
    /** 套餐显示名（aigc_plans.name，后台改名即时生效） */
    planName?: string;
    planExpiresAt?: string | null;
  };
  stats: { canvasCount: number; templateCount: number; assetCount: number; memberCount: number };
}

export interface WorkgroupMember {
  user_id: string;
  role: string;
  nickname: string | null;
  joined_at: string;
  status: string;
  phone: string | null;
  username: string;
  avatar_url: string | null;
}

export const workgroupApi = {
  /** 获取我的工作组列表 */
  list: async () => {
    const res = await authedFetch(`${getApiBase()}/workgroups`, { headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取工作组失败');
    return res.json() as Promise<{ success: true; data: WorkgroupInfo[] }>;
  },

  /** 创建工作组 */
  create: async (data: { name: string; description?: string }) => {
    const res = await authedFetch(`${getApiBase()}/workgroups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '创建工作组失败');
    return res.json() as Promise<{ success: true; data: WorkgroupInfo }>;
  },

  /** 工作组详情 */
  detail: async (id: number) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}`, { headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取工作组详情失败');
    return res.json() as Promise<{ success: true; data: WorkgroupInfo & { members: WorkgroupMember[]; stats: Record<string, number> } }>;
  },

  /** 更新工作组 */
  update: async (id: number, data: { name?: string; description?: string }) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '更新工作组失败');
    return res.json();
  },

  /** 删除工作组 */
  remove: async (id: number) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '删除工作组失败');
    return res.json();
  },

  /** 获取成员列表 */
  members: async (id: number) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}/members`, { headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取成员列表失败');
    return res.json() as Promise<{ success: true; data: WorkgroupMember[] }>;
  },

  /** 修改成员角色 */
  updateMember: async (id: number, userId: string, role: string) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}/members`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify({ userId, role }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '修改角色失败');
    return res.json();
  },

  /** 移除成员 */
  removeMember: async (id: number, userId: string) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}/members`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify({ userId }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '移除成员失败');
    return res.json();
  },

  /** 退出工作组 */
  leave: async (id: number) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}/leave`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '退出工作组失败');
    return res.json();
  },

  /** 创建邀请 */
  invite: async (id: number, data: { invitedPhone?: string; role?: string }) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '创建邀请失败');
    return res.json() as Promise<{ success: true; data: { inviteUrl: string; invite_token: string; expires_at: string } }>;
  },

  /** 邀请列表 */
  invitations: async (id: number) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}/invite`, { headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取邀请列表失败');
    return res.json();
  },

  /** 查询邀请信息（无需登录） */
  inviteInfo: async (token: string) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/invite-info?token=${token}`);
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '查询邀请信息失败');
    return res.json();
  },

  /** 接受邀请 */
  acceptInvite: async (token: string) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/accept-invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '接受邀请失败');
    return res.json();
  },

  /** 席位信息 */
  seats: async (id: number) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}/seats`, { headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取席位信息失败');
    return res.json();
  },

  /** 购买席位 — 验证并返回价格信息 */
  purchaseSeats: async (id: number, data: { seats: number; payMethod: string }) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}/seats`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '购买席位失败');
    return res.json() as Promise<{ success: true; data: { workgroupId: number; seats: number; amount: number; unitPrice: number; payMethod: string; nextStep: string } }>;
  },

  /** 设置当前工作组（前端 localStorage） */
  setCurrent: (id: number | null) => {
    if (typeof window !== 'undefined') {
      if (id) {
        localStorage.setItem('aigc-workgroup-id', String(id));
      } else {
        localStorage.removeItem('aigc-workgroup-id');
      }
    }
  },

  /** 获取当前工作组 ID */
  getCurrent: (): number | null => {
    if (typeof window === 'undefined') return null;
    const id = localStorage.getItem('aigc-workgroup-id');
    if (!id || id === 'null' || id === '0') return null;
    return parseInt(id, 10) || null;
  },

  /** 团队积分池：余额 + 消耗明细（普通成员仅见本人明细） */
  credits: async (id: number) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}/credits`, { headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取团队积分失败');
    return res.json() as Promise<{ success: true; data: WorkgroupCreditsInfo }>;
  },

  /** 已退役（2026-09-03）：个人→团队划转随独立团队池一并下线，空间消耗直接走创建者账户 */

  /** 续费/调整订阅到期时间（owner/admin，ISO 字符串；null=永久） */
  updateSubscription: async (id: number, subscriptionExpiresAt: string | null) => {
    const res = await authedFetch(`${getApiBase()}/workgroups/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify({ subscription_expires_at: subscriptionExpiresAt }),
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '更新订阅失败');
    return res.json();
  },
};

// ==================== 空间资产 API ====================

export const workspaceApi = {
  /** 当前空间总览（存储/积分/资产统计/订阅状态）；wgId 可显式指定工作组 */
  overview: async (wgId?: number) => {
    const qs = wgId ? `?wg=${wgId}` : '';
    const res = await authedFetch(`${getApiBase()}/workspace/overview${qs}`, { headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取空间总览失败');
    return res.json() as Promise<{ success: true; data: WorkspaceOverview }>;
  },

  /** 空间资产列表：canvas / media / template；wgId 可显式指定工作组；media 支持 orphanOnly 仅看孤儿媒体 */
  assets: async (type: 'canvas' | 'media' | 'template', page = 1, pageSize = 20, wgId?: number, opts?: { orphanOnly?: boolean }) => {
    const wgQs = wgId ? `&wg=${wgId}` : '';
    const orphanQs = opts?.orphanOnly ? '&orphan=1' : '';
    const res = await authedFetch(`${getApiBase()}/workspace/assets?type=${type}&page=${page}&pageSize=${pageSize}${wgQs}${orphanQs}`, { headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取空间资产失败');
    return res.json() as Promise<{
      success: true;
      data: { type: string; items: Array<Record<string, unknown>>; total: number; totalBytes?: number; page: number; pageSize: number };
    }>;
  },

  /** 账户中心：本人资源消耗视图（个人消耗 + 本人在各团队消耗明细） */
  usage: async () => {
    const res = await authedFetch(`${getApiBase()}/workspace/usage`, { headers: getHeaders() });
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).error || '获取消耗明细失败');
    return res.json() as Promise<{
      success: true;
      data: {
        personal: { consumed: number; ops: number };
        workgroups: Array<{ workgroup_id: number; workgroup_name: string; consumed: number; ops: number; last_used_at: string }>;
        recent: Array<{
          id: number; workgroup_id: number | null; workgroup_name: string | null;
          amount: number; balance_after: number; type: string; node_type: string | null;
          description: string | null; created_at: string;
        }>;
      };
    }>;
  },
};

// ═══════════════════════════════════════════════════════════════
// 资源分类体系：文件夹 / 批量移动（PRD 4.2）
// ═══════════════════════════════════════════════════════════════

export interface AssetFolder {
  id: number;
  /** 历史字段（结构反转后新建文件夹恒 null，文件夹=一级「位置」不再绑定分类） */
  topCategory: string | null;
  parentId: number | null;
  name: string;
  coverAssetId: number | null;
  createdAt: string;
}

async function apiCall<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  if (!token) throw new ApiError(401, '未登录');
  const res = await authedFetch(`${getApiBase()}${path}`, {
    ...init,
    headers: { ...getHeaders(), ...(init?.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (json as { error?: string }).error || '请求失败');
  return json as T;
}

export async function fetchFolders(): Promise<AssetFolder[]> {
  const json = await apiCall<{ success: boolean; folders: AssetFolder[] }>(`/assets/folders`);
  return json.folders || [];
}

export async function createFolder(data: { name: string; parentId?: number | null }): Promise<{ id: number }> {
  return apiCall('/assets/folders', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateFolder(id: number, data: { name?: string; coverAssetId?: number | null; parentId?: number | null }): Promise<{ success: boolean }> {
  return apiCall(`/assets/folders/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function deleteFolder(id: number): Promise<{ success: boolean }> {
  return apiCall(`/assets/folders/${id}`, { method: 'DELETE' });
}

export async function moveAssets(data: { assetIds: number[]; folderId?: number | null; topCategory?: string | null }): Promise<{ success: boolean; moved: number }> {
  return apiCall('/assets/move', { method: 'POST', body: JSON.stringify(data) });
}

// ═══════════════════════════════════════════════════════════════
// 主体角色库（PRD 4.3）
// ═══════════════════════════════════════════════════════════════

export interface SubjectItem {
  id: number;
  name: string;
  description: string;
  prompt: string;
  tags: string[];
  refImages: string[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export async function fetchSubjects(tag?: string): Promise<SubjectItem[]> {
  const params = tag ? `?tag=${encodeURIComponent(tag)}` : '';
  const json = await apiCall<{ success: boolean; subjects: SubjectItem[] }>(`/subjects${params}`);
  return json.subjects || [];
}

export async function createSubject(data: { name: string; description?: string; prompt?: string; tags?: string[]; refImages?: string[] }): Promise<{ id: number }> {
  return apiCall('/subjects', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateSubject(id: number, data: Partial<{ name: string; description: string; prompt: string; tags: string[]; refImages: string[] }>): Promise<{ success: boolean }> {
  return apiCall(`/subjects/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function deleteSubject(id: number): Promise<{ success: boolean }> {
  return apiCall(`/subjects/${id}`, { method: 'DELETE' });
}

// ═══════════════════════════════════════════════════════════════
// 站内信（用户侧）
// ═══════════════════════════════════════════════════════════════

export interface MessageItem {
  id: number;
  title: string;
  content: string;
  type: string;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export async function fetchMessages(limit = 50, offset = 0): Promise<{ messages: MessageItem[]; unreadCount: number }> {
  const json = await apiCall<{ success: boolean; messages: MessageItem[]; unreadCount: number }>(`/messages?limit=${limit}&offset=${offset}`);
  return { messages: json.messages || [], unreadCount: json.unreadCount || 0 };
}

export async function markMessagesRead(data: { ids?: number[]; all?: boolean }): Promise<{ success: boolean }> {
  return apiCall('/messages', { method: 'POST', body: JSON.stringify(data) });
}

// ═══════════════════════════════════════════════════════════════
// 发票（用户侧）
// ═══════════════════════════════════════════════════════════════

export interface InvoiceItem {
  id: number;
  orderId: number;
  orderIds: number[];
  orderNo: string | null;
  amount: number;
  titleType: string;
  title: string;
  taxNo: string | null;
  invoiceType: 'normal' | 'special';
  addr: string | null;
  phone: string | null;
  bank: string | null;
  account: string | null;
  email: string;
  remark: string | null;
  status: 'pending' | 'issued' | 'rejected' | 'cancelled';
  adminNote: string | null;
  hasFile: boolean;
  fileName: string | null;
  issuedAt: string | null;
  createdAt: string;
}

export interface InvoiceOrder {
  id: number;
  orderNo: string;
  amount: number;
  orderType: string;
  paidAt: string | null;
  createdAt: string;
  /** 开票状态：none=未开票（可申请）/ pending=开票中（可撤回）/ issued=已开票（可下载） */
  invoiceStatus: 'none' | 'pending' | 'issued';
  invoiceId: number | null;
  /** 该笔有效申请合并的订单数（>1 表示与其他订单合并开票） */
  invoiceCount: number;
  hasFile: boolean;
}

export interface InvoiceTitleItem {
  id: number;
  titleType: 'personal' | 'company';
  title: string;
  taxNo: string | null;
  addr: string | null;
  phone: string | null;
  bank: string | null;
  account: string | null;
  isDefault: boolean;
  createdAt: string;
}

export async function fetchMyInvoices(): Promise<{ invoices: InvoiceItem[]; orders: InvoiceOrder[] }> {
  const json = await apiCall<{ success: boolean; invoices: InvoiceItem[]; orders: InvoiceOrder[] }>('/invoices');
  return { invoices: json.invoices || [], orders: json.orders || [] };
}

/** 撤回开票申请（仅 pending 状态可撤回） */
export async function cancelInvoice(id: number): Promise<void> {
  await apiCall(`/invoices/${id}/cancel`, { method: 'POST' });
}

export async function applyInvoice(data: {
  orderIds: number[];
  titleType: 'personal' | 'company';
  title: string;
  taxNo?: string;
  invoiceType?: 'normal' | 'special';
  addr?: string;
  phone?: string;
  bank?: string;
  account?: string;
  email: string;
  remark?: string;
}): Promise<{ id: number; amount: number }> {
  return apiCall('/invoices', { method: 'POST', body: JSON.stringify(data) });
}

export async function fetchInvoiceTitles(): Promise<InvoiceTitleItem[]> {
  const json = await apiCall<{ success: boolean; titles: InvoiceTitleItem[] }>('/invoices/titles');
  return json.titles || [];
}

export async function createInvoiceTitle(data: {
  titleType: 'personal' | 'company';
  title: string;
  taxNo?: string;
  addr?: string;
  phone?: string;
  bank?: string;
  account?: string;
  isDefault?: boolean;
}): Promise<InvoiceTitleItem> {
  const json = await apiCall<{ success: boolean; title: InvoiceTitleItem }>('/invoices/titles', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return json.title;
}

export async function updateInvoiceTitle(
  id: number,
  data: {
    titleType: 'personal' | 'company';
    title: string;
    taxNo?: string;
    addr?: string;
    phone?: string;
    bank?: string;
    account?: string;
    isDefault?: boolean;
  }
): Promise<InvoiceTitleItem> {
  const json = await apiCall<{ success: boolean; title: InvoiceTitleItem }>(`/invoices/titles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return json.title;
}

export async function deleteInvoiceTitle(id: number): Promise<void> {
  await apiCall(`/invoices/titles/${id}`, { method: 'DELETE' });
}

export async function validateInvoiceTitle(data: { title: string; taxNo: string }): Promise<{ valid: boolean; message: string }> {
  return apiCall('/invoices/validate-title', { method: 'POST', body: JSON.stringify(data) });
}

// ==================== 内容广场 API（2026-10-04） ====================

export interface PlazaCategoryDto {
  id: number;
  name: string;
  sortOrder: number;
}

export interface PlazaPostDto {
  id: number;
  workflowId: number;
  workflowName: string | null;
  title: string;
  description: string | null;
  coverUrl: string | null;
  /** 成品视频 serve URL（2026-10-06，详情页播放器 + 卡片 hover 预览） */
  mediaUrl: string | null;
  /** 是否公开制作过程（2026-10-06，false 时他人不可查看快照/复刻） */
  shareProcess: boolean;
  categoryId: number | null;
  categoryName: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'offline';
  reviewNote: string | null;
  nodeCount: number;
  viewCount: number;
  cloneCount: number;
  /** 点赞数（2026-10-05 社区互动）；likedByMe=当前浏览者是否已赞（未登录恒 false） */
  likeCount: number;
  likedByMe: boolean;
  createdAt: string;
  author: {
    userId: string;
    nickname: string | null;
    avatarUrl: string | null;
    /** 作者已过审作品数（2026-10-07 播放页改版，仅 detail 接口返回） */
    postCount?: number;
    /** 作者全部作品获赞总数（2026-10-07，仅 detail 接口返回） */
    likeTotal?: number;
  };
}

/** 作者公开主页资料（匿名可访；仅昵称/头像/公开统计） */
export interface PlazaAuthorProfileDto {
  userId: string;
  nickname: string | null;
  avatarUrl: string | null;
  postCount: number;
  likeTotal: number;
  /** 全部作品播放总数（作者页 v2 三统计卡） */
  viewTotal: number;
}

export interface PlazaFeedResponse {
  success: boolean;
  data: { total: number; posts: PlazaPostDto[]; categories: PlazaCategoryDto[]; author?: PlazaAuthorProfileDto | null };
}

export const plazaApi = {
  /** 广场流（approved，分类筛选 + new/hot/clone 排序 + keyword 搜索 + author 作者主页过滤）；未登录也可拉取 */
  feed: async (params?: {
    categoryId?: number;
    sort?: 'new' | 'hot' | 'clone';
    keyword?: string;
    /** 作者主页（2026-10-07）：传 userId 只看该作者 approved 帖，响应附 author 公开资料 */
    author?: string;
    limit?: number;
    offset?: number;
  }): Promise<PlazaFeedResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.categoryId) searchParams.set('categoryId', String(params.categoryId));
    if (params?.sort) searchParams.set('sort', params.sort);
    if (params?.keyword?.trim()) searchParams.set('keyword', params.keyword.trim());
    if (params?.author?.trim()) searchParams.set('author', params.author.trim());
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    return apiRequest<PlazaFeedResponse>(`/plaza${qs ? `?${qs}` : ''}`);
  },

  /** 点赞/取消点赞切换（登录必须；返回切换后状态） */
  like: async (postId: number): Promise<{ success: boolean; data: { liked: boolean; likeCount: number } }> => {
    return apiRequest(`/plaza/${postId}/like`, { method: 'POST', body: JSON.stringify({}) });
  },

  /** 评论列表（公开；仅 approved 帖） */
  comments: async (postId: number): Promise<{
    success: boolean;
    data: { comments: Array<{ id: number; content: string; createdAt: string; author: { userId: string; nickname: string | null; avatarUrl: string | null } }>; total: number };
  }> => apiRequest(`/plaza/${postId}/comments`),

  /** 发表评论（登录；500 字上限） */
  addComment: async (postId: number, content: string): Promise<{
    success: boolean;
    data: { id: number; createdAt: string };
  }> => apiRequest(`/plaza/${postId}/comments`, { method: 'POST', body: JSON.stringify({ content }) }),

  /** 作品详情（approved 公开；作者/admin 可看任意状态自己的帖）。includeSnapshot=true 附带制作过程快照（shareProcess=false 且非作者/admin 时 403） */
  detail: async (id: number, includeSnapshot = false): Promise<{
    success: boolean;
    data: PlazaPostDto & {
      isOwner: boolean;
      /** 是否公开制作过程（2026-10-06）；false 时他人不可查看快照/复刻 */
      shareProcess?: boolean;
      snapshot?: { nodes: unknown[]; edges: unknown[] };
      updatedAt: string;
    };
  }> => {
    const qs = includeSnapshot ? '?snapshot=1' : '';
    return apiRequest(`/plaza/${id}${qs}`);
  },

  /** 当前工作流的发布状态（画布页「发布与分享」菜单回显；未发布返回 data=null） */
  myPostByWorkflow: async (workflowId: number): Promise<{
    success: boolean;
    data: (PlazaPostDto & { reviewNote: string | null }) | null;
  }> => {
    return apiRequest(`/plaza/mine?workflowId=${workflowId}`);
  },

  /** 我的发布列表 */
  myPosts: async (): Promise<{ success: boolean; data: PlazaPostDto[] }> => {
    return apiRequest('/plaza/mine');
  },

  /** 发布/修改重提（服务端从源工作流读快照，前端只传元信息；categoryId=发布者建议分类） */
  publish: async (data: {
    workflowId: number;
    title: string;
    description?: string;
    coverUrl?: string | null;
    mediaUrl?: string | null;
    shareProcess?: boolean;
    categoryId?: number | null;
  }): Promise<{ success: boolean; data: { id: number; resubmitted: boolean } }> => {
    return apiRequest('/plaza/publish', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** 广场分类列表（公开匿名可读，2026-10-08：发布弹窗分类下拉候选） */
  categories: async (): Promise<{ success: boolean; data: PlazaCategoryDto[] }> => {
    return apiRequest('/plaza/categories');
  },

  /** 撤回（下架自己的作品） */
  withdraw: async (postId: number): Promise<{ success: boolean; message: string }> => {
    return apiRequest(`/plaza/${postId}/withdraw`, { method: 'POST', body: JSON.stringify({}) });
  },

  /** 克隆广场作品到我的画布（服务端从快照建副本工作流） */
  clone: async (postId: number): Promise<{ success: boolean; data: { id: number } }> => {
    return apiRequest(`/plaza/${postId}/clone`, { method: 'POST', body: JSON.stringify({}) });
  },

  /** 画布内可选封面候选（近期图片资产）+ 成品视频候选（2026-10-06） */
  coverCandidates: async (workflowId: number): Promise<{
    success: boolean;
    data: {
      assets: { url: string; type: string; label: string | null }[];
      videos?: { url: string; label: string | null }[];
    };
  }> => {
    return apiRequest(`/plaza/cover-candidates?workflowId=${workflowId}`);
  },

  /** 上传自定义封面（PNG/JPG/WebP ≤10MB，走 TOS 统一链路，返回 serve URL） */
  /** 上传广场媒体（图片封面 ≤10MB / 成品视频 ≤100MB），返回 TOS serve URL（2026-10-06） */
  /* 广场媒体上传（2026-10-20b 流式直通 + XHR 进度）：
   * - body=文件本体（raw，免 multipart 双缓冲），后端边收边转 TOS（速度≈用户上行，不再串行两段）
   * - XHR upload.onprogress 报进度：percent 封顶 99（数据全部离开浏览器后进入「转存素材库」阶段，由 phase 标记）
   * - speedBps 为 400ms 采样的指数平滑值，etaSeconds=(total-loaded)/speed */
  /**
   * 广场成品/封面上传（2026-10-20 双通道）：
   * 1. 首选「浏览器 TOS 预签名直传」——绕过服务器入带宽瓶颈（~10Mbps 级），用户本地
   *    上行带宽（如 10MB/s）直达 TOS 机房，速度提升可达 10 倍；服务器零文件流量
   * 2. 直传失败（CORS 被网关拦/预签名失败/网络策略）自动回退「服务器中转」（raw 流式）
   * 3. abortRef.current 挂载取消函数——发布弹窗关闭时调用中止（服务端流随之断开）
   */
  uploadMedia: (
    file: File,
    onProgress?: (p: { percent: number; loadedBytes: number; totalBytes: number; speedBps: number; etaSeconds: number | null; phase: 'upload' | 'finalize' }) => void,
    abortRef?: { current?: (() => void) | null }
  ): Promise<{ success: boolean; data: { url: string; type: 'image' | 'video' } }> => {
    const extFromName = (() => {
      const m = /\.([a-z0-9]+)$/i.exec(file.name);
      return m ? m[1].toLowerCase() : '';
    })();
    const ext = extFromName || (file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : file.type === 'video/webm' ? 'webm' : file.type === 'video/quicktime' ? 'mov' : file.type === 'video/x-matroska' ? 'mkv' : file.type === 'image/jpeg' ? 'jpg' : file.type === 'video/mp4' ? 'mp4' : 'bin');
    const kind = /^(png|jpg|jpeg|webp)$/.test(ext) ? 'image' : 'video';

    const attemptDirect = async (): Promise<{ success: boolean; data: { url: string; type: 'image' | 'video' } }> => {
      const pr = await fetch(`${getBasePath()}/api/plaza/media-upload/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
        body: JSON.stringify({ kind, ext, size: file.size }),
      });
      const pj = (await pr.json().catch(() => ({}))) as { success?: boolean; error?: string; data?: { uploadUrl: string; objectKey: string } };
      if (!pr.ok || !pj?.success || !pj?.data?.uploadUrl) throw new Error(pj?.error || 'presign_failed');

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', pj.data!.uploadUrl);
        xhr.timeout = 0;
        if (abortRef) abortRef.current = () => xhr.abort();
        let lastT = 0;
        let lastLoaded = 0;
        let emaSpeed = 0;
        xhr.upload.onprogress = (e) => {
          if (!onProgress || !e.lengthComputable) return;
          const now = performance.now();
          if (lastT && now - lastT >= 400) {
            const inst = (e.loaded - lastLoaded) / ((now - lastT) / 1000);
            emaSpeed = emaSpeed > 0 ? emaSpeed * 0.6 + inst * 0.4 : inst;
            lastT = now;
            lastLoaded = e.loaded;
          } else if (!lastT) {
            lastT = now;
          }
          const done = e.loaded >= e.total;
          onProgress({
            percent: done ? 99 : Math.min(99, Math.round((e.loaded / e.total) * 100)),
            loadedBytes: e.loaded,
            totalBytes: e.total,
            speedBps: emaSpeed,
            etaSeconds: emaSpeed > 0 ? Math.round((e.total - e.loaded) / emaSpeed) : null,
            phase: done ? 'finalize' : 'upload',
          });
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`TOS 直传失败 (HTTP ${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error('TOS 直传网络错误'));
        xhr.ontimeout = () => reject(new Error('TOS 直传超时'));
        xhr.onabort = () => reject(Object.assign(new Error('已取消上传'), { name: 'AbortError' }));
        xhr.send(file);
      });

      const cr = await fetch(`${getBasePath()}/api/plaza/media-upload/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
        body: JSON.stringify({ objectKey: pj.data!.objectKey, kind, ext, size: file.size, fileName: file.name }),
      });
      const cj = (await cr.json().catch(() => ({}))) as { success?: boolean; error?: string; data?: { url: string; type: 'image' | 'video' } };
      if (!cr.ok || !cj?.success || !cj?.data) throw new Error(cj?.error || '登记失败');
      return cj as { success: boolean; data: { url: string; type: 'image' | 'video' } };
    };

    const attemptRelay = (): Promise<{ success: boolean; data: { url: string; type: 'image' | 'video' } }> =>
      new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${getBasePath()}/api/plaza/media-upload`);
        xhr.responseType = 'json';
        const token = getToken();
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('x-tos-ext', ext);
        xhr.setRequestHeader('x-tos-kind', kind);
        xhr.timeout = 0; // 大文件不限时（进度由 onprogress 呈现，错误由 onerror/ontimeout 捕获）

        let lastT = 0;
        let lastLoaded = 0;
        let emaSpeed = 0;
        xhr.upload.onprogress = (e) => {
          if (!onProgress || !e.lengthComputable) return;
          const now = performance.now();
          if (lastT && now - lastT >= 400) {
            const inst = (e.loaded - lastLoaded) / ((now - lastT) / 1000);
            emaSpeed = emaSpeed > 0 ? emaSpeed * 0.6 + inst * 0.4 : inst;
            lastT = now;
            lastLoaded = e.loaded;
          } else if (!lastT) {
            lastT = now;
          }
          const done = e.loaded >= e.total;
          onProgress({
            percent: done ? 99 : Math.min(99, Math.round((e.loaded / e.total) * 100)),
            loadedBytes: e.loaded,
            totalBytes: e.total,
            speedBps: emaSpeed,
            etaSeconds: emaSpeed > 0 ? Math.round((e.total - e.loaded) / emaSpeed) : null,
            phase: done ? 'finalize' : 'upload',
          });
        };
        xhr.onerror = () => reject(new Error('网络错误，上传中断，请重试'));
        xhr.ontimeout = () => reject(new Error('上传超时，请检查网络后重试'));
        xhr.onabort = () => reject(Object.assign(new Error('已取消上传'), { name: 'AbortError' }));
        xhr.onload = () => {
          const j = (xhr.response || {}) as { success?: boolean; error?: string; data?: { url: string; type: 'image' | 'video' } };
          if (xhr.status >= 200 && xhr.status < 300 && j?.success && j?.data) {
            resolve(j as { success: boolean; data: { url: string; type: 'image' | 'video' } });
          } else {
            reject(new Error(j?.error || '上传失败，请重试'));
          }
        };
        if (abortRef) abortRef.current = () => xhr.abort();
        xhr.send(file);
      });

    return attemptDirect().catch((err: unknown) => {
      const e = err as { name?: string; message?: string };
      if (e?.name === 'AbortError') throw err;
      // 直传链路不可用（CORS/预签名/网络）→ 回退服务器中转，进度语义不变
      return attemptRelay();
    });
  },
};

/* ═══ 站点横幅/轮播（2026-10-08：首页横幅 + 登录轮播，后台 BannersTab 管理）═══ */

export interface SiteBannerDto {
  id: number;
  type: 'home_banner' | 'login_carousel';
  title: string;
  subtitle: string;
  imageUrl: string;
  linkUrl: string;
  sortOrder: number;
  enabled: boolean;
}

export const bannersApi = {
  /** 公开读取（匿名可读）——登录页/首页横幅接入 */
  list: async (type: 'home_banner' | 'login_carousel'): Promise<SiteBannerDto[]> => {
    try {
      const r = await fetch(`${getBasePath()}/api/banners?type=${type}`, { cache: 'no-store' });
      if (!r.ok) return [];
      const j = await r.json();
      return j?.data || [];
    } catch {
      return []; // 静默降级：调用方回退内置默认内容
    }
  },

  /** 后台管理（requireAdmin） */
  adminList: async (): Promise<SiteBannerDto[]> => {
    const r = await authedFetch(`${getApiBase()}/admin/banners`, { headers: adminHeaders() });
    if (!r.ok) throw new ApiError(r.status, (await r.json().catch(() => ({}))).error || '获取横幅列表失败');
    const j = await r.json();
    return j?.data || [];
  },
  create: async (input: Partial<SiteBannerDto> & { type: 'home_banner' | 'login_carousel'; imageUrl: string }): Promise<SiteBannerDto> => {
    const r = await authedFetch(`${getApiBase()}/admin/banners`, {
      method: 'POST',
      headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!r.ok) throw new ApiError(r.status, (await r.json().catch(() => ({}))).error || '新增横幅失败');
    const j = await r.json();
    return j.data;
  },
  update: async (input: Partial<SiteBannerDto> & { id: number }): Promise<SiteBannerDto> => {
    const r = await authedFetch(`${getApiBase()}/admin/banners`, {
      method: 'PUT',
      headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!r.ok) throw new ApiError(r.status, (await r.json().catch(() => ({}))).error || '更新横幅失败');
    const j = await r.json();
    return j.data;
  },
  remove: async (id: number): Promise<void> => {
    const r = await authedFetch(`${getApiBase()}/admin/banners?id=${id}`, { method: 'DELETE', headers: adminHeaders() });
    if (!r.ok) throw new ApiError(r.status, (await r.json().catch(() => ({}))).error || '删除横幅失败');
  },
};

// ==================== admin 内容广场审核 API（2026-10-04） ====================

export const adminPlazaApi = {
  list: async (params?: {
    status?: 'pending' | 'approved' | 'rejected' | 'offline' | 'all';
    limit?: number;
    offset?: number;
  }): Promise<{ success: boolean; data: { total: number; posts: PlazaPostDto[] } }> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    return apiRequest(`/admin/plaza${qs ? `?${qs}` : ''}`);
  },

  /** 审核通过（必须指定分类） */
  approve: async (postId: number, categoryId: number): Promise<{ success: boolean; message: string }> => {
    return apiRequest(`/admin/plaza/${postId}/review`, {
      method: 'POST',
      body: JSON.stringify({ action: 'approve', categoryId }),
    });
  },

  /** 驳回（必须填原因，作者可修改后重提） */
  reject: async (postId: number, note: string): Promise<{ success: boolean; message: string }> => {
    return apiRequest(`/admin/plaza/${postId}/review`, {
      method: 'POST',
      body: JSON.stringify({ action: 'reject', note }),
    });
  },

  /** 下架已通过作品 */
  offline: async (postId: number, reason: string): Promise<{ success: boolean; message: string }> => {
    return apiRequest(`/admin/plaza/${postId}/review`, {
      method: 'POST',
      body: JSON.stringify({ action: 'offline', reason }),
    });
  },

  categories: async (): Promise<{ success: boolean; data: PlazaCategoryDto[] }> => {
    return apiRequest('/admin/plaza/categories');
  },

  saveCategory: async (data: {
    id?: number;
    name: string;
    sortOrder: number;
  }): Promise<{ success: boolean; data: PlazaCategoryDto }> => {
    return apiRequest('/admin/plaza/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  deleteCategory: async (id: number): Promise<{ success: boolean; message: string }> => {
    return apiRequest(`/admin/plaza/categories?id=${id}`, { method: 'DELETE' });
  },
};
